import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Modal, ModalContent, ModalHeader, ModalFooter } from '../shared/Modal';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { REFUND_DAYS } from '../../config/constants';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({ isOpen, onClose }) => {
  const { darkMode } = useTheme();
  const { handleDeleteAccount, userProfile } = useAuth();

  // Only a paid purchase gets the stronger warning. Comped Pro (beta users)
  // has no purchase date and keeps the plain "ends Pro access" line.
  const purchasedAt = userProfile?.payment_tier === 'pro' && userProfile.pro_purchased_at
    ? new Date(userProfile.pro_purchased_at)
    : null;
  const withinRefundWindow = purchasedAt !== null &&
    Date.now() - purchasedAt.getTime() < REFUND_DAYS * 24 * 60 * 60 * 1000;
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      await handleDeleteAccount();
      // User will be signed out and redirected by AuthContext
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete account');
      setIsDeleting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={isDeleting ? () => {} : onClose} maxWidth="sm">
      <ModalContent>
        <ModalHeader
          title="Delete Account"
          icon={
            <div className="mx-auto w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center">
              <AlertTriangle size={28} className="text-red-500" />
            </div>
          }
        />

        <div className={`space-y-3 text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
          <p>This action is <strong className={darkMode ? 'text-white' : 'text-slate-900'}>permanent and cannot be undone</strong>. Deleting your account will:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Remove your profile from our database</li>
            <li>End Pro access on this account</li>
            <li>Delete your authentication credentials</li>
          </ul>
          <p className={`p-3 rounded-lg ${darkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
            Your Google Sheet will <strong className={darkMode ? 'text-white' : 'text-slate-900'}>not</strong> be deleted — it remains in your Google Drive.
          </p>
          {purchasedAt && (
            <div className={`p-3 rounded-lg space-y-2 ${darkMode ? 'bg-amber-500/10 border border-amber-500/30 text-amber-100' : 'bg-amber-50 border border-amber-200 text-amber-900'}`}>
              <p>
                You purchased TallyUp Pro. Deleting your account ends it. If you sign up again later, you&apos;ll start on Free.
              </p>
              {withinRefundWindow && (
                <p>
                  Want your money back instead? Email{' '}
                  <a href="mailto:support@tallyup.finance" className="underline font-semibold">support@tallyup.finance</a>
                  {' '}for a refund before deleting.
                </p>
              )}
            </div>
          )}
        </div>

        {error && (
          <div className="mt-4 p-3 rounded-lg bg-red-500/20 border border-red-500/50">
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        <ModalFooter>
          <button
            onClick={onClose}
            disabled={isDeleting}
            className={`flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm transition-colors ${
              darkMode
                ? 'bg-white/10 hover:bg-white/20 text-white'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-800'
            } ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isDeleting}
            className={`flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm text-white transition-colors ${
              isDeleting
                ? 'bg-red-500/50 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {isDeleting ? 'Deleting...' : 'Delete My Account'}
          </button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
